import { Position } from '@shared/models/position';

declare global {
  interface Array<T> {
    move(from: number, to: number): void;

    moveRelativeTo(
      this: (T & { id: string | number })[],
      id: string | number,
      referenceId: string | number,
      position: Position
    ): (T & { id: string | number })[];

    equalsWith<S>(
      second: S[],
      compareFn?: (first: T, second: S) => boolean
    ): boolean;

    pushIfNotIncludes(element: number | string): void;

    naturalSort(propertyName: string, direction: 'asc' | 'desc'): T[];

    hasElements(): boolean;
  }
}

Array.prototype.move = function (from: number, to: number): void {
  if (from < 0 && to < 0) {
    return;
  }

  this.splice(to < 0 ? 0 : to, 0, this.splice(from, 1)[0]);
};

/**
 * Moves an item with the given id after or before the given reference id.
 * The function does not alter the original array.
 */
Array.prototype.moveRelativeTo = function <T extends { id: string | number }>(
  id: string | number,
  referenceId: string | number,
  position: Position
): T[] {
  const element = (this as T[]).find(x => x.id === id);

  if (!element) {
    return [...(this as T[])];
  }

  const result = (this as T[]).filter(x => x.id !== id);

  const referenceIndex = result.findIndex(x => x.id === referenceId);

  if (position === Position.After) {
    result.splice(referenceIndex === -1 ? 0 : referenceIndex + 1, 0, element);
  } else {
    result.splice(referenceIndex === -1 ? 0 : referenceIndex, 0, element);
  }

  return result;
};

Array.prototype.equalsWith = function <T, S>(
  second: S[],
  compareFn?: (x: T, y: S) => boolean
): boolean {
  const fn =
    compareFn ??
    ((x: T, y: S): boolean => JSON.stringify(x) === JSON.stringify(y));

  return (
    this?.length === second?.length &&
    this?.every((x: T) => second?.find(y => fn(x, y)) != null)
  );
};

Array.prototype.pushIfNotIncludes = function (element: number | string): void {
  if (!this.includes(element)) {
    this.push(element);
  }
};

Array.prototype.naturalSort = function <T>(
  propertyName: string,
  direction: 'asc' | 'desc'
): T[] {
  if (!propertyName || propertyName.trim() === '') {
    return this;
  }

  const genericOptions = (this || []).filter(x => x?.keepOnTop);

  const sortedList = [...this]
    .filter(x => !x?.keepOnTop)
    .sort((a, b) => {
      if (direction === 'asc') {
        return a[propertyName].localeCompare(
          b[propertyName],
          navigator.languages[0] || navigator.language,
          {
            numeric: true,
            ignorePunctuation: true
          }
        );
      } else {
        return b[propertyName].localeCompare(
          a[propertyName],
          navigator.languages[0] || navigator.language,
          {
            numeric: true,
            ignorePunctuation: true
          }
        );
      }
    });

  return [...genericOptions, ...sortedList];
};

Array.prototype.hasElements = function (): boolean {
  return (this ?? []).length > 0;
};

export {};
